import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Modal, Btn } from '@/design-system';

interface ScenarioNameModalProps {
  open: boolean;
  title: string;
  initialName: string;
  /** Noms interdits (les AUTRES scénarios) — anti-doublon insensible à la casse. */
  existingNames: string[];
  submitLabel: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
}

// Modale de nom partagée renommage/duplication (option UX validée par Pablo :
// ⋮ sur chaque carte + menu du scénario ouvert, une seule modale commune).
export function ScenarioNameModal({
  open, title, initialName, existingNames, submitLabel, onClose, onSubmit,
}: ScenarioNameModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  useEffect(() => { if (open) setName(initialName); }, [open, initialName]);

  const trimmed = name.trim();
  const isDuplicate = existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase());
  const isValid = trimmed.length > 0 && !isDuplicate;
  const submit = () => {
    if (!isValid) return;
    onSubmit(trimmed);
    onClose();
  };

  if (!open) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Btn variant="secondary" label={t('actions.cancel')} onClick={onClose} />
          <Btn variant="primary" label={submitLabel} onClick={submit} disabled={!isValid} />
        </>
      }
    >
      <div>
        <label style={{
          fontFamily: sans, fontSize: 11, fontWeight: 600,
          color: P.t2, display: 'block', marginBottom: 4,
        }}>
          {t('budget.scenario.create.name')}
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
          onFocus={e => e.target.select()}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          style={{
            width: '100%', height: 40, padding: '0 12px',
            fontFamily: sans, fontSize: 14, color: P.t1,
            background: P.s3, border: `1px solid ${P.border}`,
            borderRadius: 6, outline: 'none', boxSizing: 'border-box',
          }}
        />
        {isDuplicate && (
          <span style={{ fontFamily: sans, fontSize: 10, color: P.neg, marginTop: 4, display: 'block' }}>
            {t('budget.scenario.create.nameUnique')}
          </span>
        )}
      </div>
    </Modal>
  );
}
